import Box from "@mui/material/Box";
import NodeSkeleton from "./NodeSkeleton";
import Divider from "@mui/material/Divider";
import List from "@mui/material/List";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { memo } from "react";
import { type NodeProps } from "@xyflow/react";
import type { ExplorerNode } from "../../declarations/IExplorerTypes";
import { getNodeColor, getNodeIcon } from "./nodeColors";
import NodeListCard from "./NodeListCard";
import NodeListRow, { NodeRowChevron } from "./NodeListRow";
import NodeToolbar from "./NodeToolbar";

function ServiceOrderLinesNode({ data }: NodeProps<ExplorerNode>) {
    const groups = data.lineGroups ?? [];
    const isLoading = data.status === "loading";
    const nodeColor = getNodeColor(data.entityType);

    return (
        <NodeListCard
            nodeColor={nodeColor}
            isSelected={data.isSelected}
            isLoading={isLoading}
            width={260}
        >
            <NodeToolbar
                nodeColor={nodeColor}
                icon={getNodeIcon(data.entityType)}
                title={data.nodeTitle}
                subtitle={data.nodeSubtitle}
                dataAreaId={data.dataAreaId}
                onInfoClick={data.onSelect ? () => data.onSelect!(data.selectId ?? data.entityType) : undefined}
            />

            {isLoading ? (
                <NodeSkeleton rows={4} variant="list" />
            ) : groups.length === 0 ? (
                <Typography variant="caption" color="text.secondary">
                    No lines found
                </Typography>
            ) : (
                <List dense disablePadding sx={{ mt: 0.5 }}>
                    {groups.map((group, gIndex) => (
                        <React.Fragment key={group.groupKey}>
                            {gIndex > 0 && <Divider sx={{ mx: 0.5, opacity: 0.4 }} />}
                            <NodeListRow
                                nodeColor={nodeColor}
                                isExpanded={group.isExpanded}
                                onClick={() => data.onLineGroupSelect?.(group.groupKey)}
                            >
                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                    <Typography variant="caption" fontWeight={500} sx={{ lineHeight: 1.3 }}>
                                        {group.externalItemNumber}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.3 }}>
                                        ({group.items.length})
                                    </Typography>
                                    <Box sx={{ flex: 1 }} />
                                    <NodeRowChevron isExpanded={group.isExpanded} nodeColor={nodeColor} />
                                </Stack>
                            </NodeListRow>

                            {group.isExpanded && (
                                <Box sx={{ pl: 1.5, pr: 0.5, pb: 0.5 }}>
                                    {group.items.map((line) => (
                                        <NodeListRow
                                            key={line.lineKey ?? line.lineNumber}
                                            nodeColor={nodeColor}
                                            isExpanded={line.isExpanded}
                                            density="compact"
                                            onClick={() => {
                                                if (data.onLineKeySelect && line.lineKey) {
                                                    data.onLineKeySelect(line.lineKey);
                                                } else {
                                                    data.onLineSelect?.(line.lineNumber);
                                                }
                                            }}
                                        >
                                            <Tooltip
                                                title={line.lineDescription || ""}
                                                arrow
                                                disableHoverListener={!line.lineDescription}
                                            >
                                                <Stack direction="row" alignItems="center" spacing={0.5}>
                                                    <Typography
                                                        variant="caption"
                                                        fontWeight={400}
                                                        sx={{ lineHeight: 1.3, fontSize: "0.7rem" }}
                                                    >
                                                        {line.itemNumber}
                                                    </Typography>
                                                    <Box sx={{ flex: 1 }} />
                                                    <NodeRowChevron isExpanded={line.isExpanded} nodeColor={nodeColor} size={14} />
                                                </Stack>
                                            </Tooltip>
                                        </NodeListRow>
                                    ))}
                                </Box>
                            )}
                        </React.Fragment>
                    ))}
                </List>
            )}
        </NodeListCard>
    );
}

export default memo(ServiceOrderLinesNode);
